const TAG_SOURCE =
  "\\(\\{([^{}()]+)\\}(?:(?:\\{before=([^{}()]*)\\})?([^{}()]*) OR \\{fallback=([^{}()]*)\\})?\\)";
const TAG_PATTERN = new RegExp(TAG_SOURCE, "g");
const EXACT_TAG = new RegExp("^" + TAG_SOURCE + "$");
const CHIP_HTML =
  /<span\b[^>]*\bdata-merge-tag="([^"]*)"[^>]*>(?:<span\b[^>]*>[^<]*<\/span>|[^<])*<\/span>/g;

export const FIELD_KEY = /^person\.(fields|field_values|field_labels)\.(.+)$/;
const NAMED_LABELS = { "parameter=": "Param", "window.": "Variable" };

export const parseMergeTag = (text) => {
  const match = String(text).match(EXACT_TAG);
  return (
    match && {
      key: match[1],
      before: match[2],
      after: match[3],
      fallback: match[4],
    }
  );
};

export const serializeMergeTag = ({ key, before, after, fallback }) => {
  const surround = before || after;
  const fallbackText = fallback === undefined && surround ? "" : fallback;
  return (
    "({" +
    key +
    "}" +
    (before ? "{before=" + before + "}" : "") +
    (after || "") +
    (fallbackText === undefined ? "" : " OR {fallback=" + fallbackText + "}") +
    ")"
  );
};

export const namedPrefix = (key) =>
  Object.keys(NAMED_LABELS).find((prefix) => key.startsWith(prefix));

export const fieldKey = (format, slug) => "person." + format + "." + slug;

const mergeTagIdentity = (key) => {
  const field = key.match(FIELD_KEY);
  if (field) return "field:" + field[2];
  return namedPrefix(key) || key;
};

export const findMergeTagOption = (groups, key) => {
  const identity = mergeTagIdentity(key);
  for (const group of groups) {
    const option = group.options.find(
      (o) => mergeTagIdentity(parseMergeTag(o.value).key) === identity
    );
    if (option) return { group, option };
  }
  return null;
};

export const mergeTagChipLabel = (key, groups) => {
  const field = key.match(FIELD_KEY);
  if (field) {
    return (
      "Field: " +
      field[2] +
      ({ field_values: " (Value)", fields: " (Legacy)" }[field[1]] || "")
    );
  }
  const prefix = namedPrefix(key);
  if (prefix) return NAMED_LABELS[prefix] + ": " + key.slice(prefix.length);

  const found = findMergeTagOption(groups, key);
  if (!found) return key;
  return (
    (found.group.chipPrefix || found.group.label) + ": " + found.option.label
  );
};

export const setMergeTagChip = (chip, text, groups) => {
  chip.dataset.mergeTag = text;
  chip.firstChild.nodeValue = mergeTagChipLabel(
    parseMergeTag(text).key,
    groups
  );
};

export const createMergeTagChip = (doc, text, groups) => {
  const chip = doc.createElement("span");
  chip.className = "cf-merge-chip fr-deletable";
  chip.setAttribute("contenteditable", "false");
  const remove = doc.createElement("span");
  remove.className = "cf-merge-chip-remove";
  remove.setAttribute("aria-hidden", "true");
  chip.append("", remove);
  setMergeTagChip(chip, text, groups);
  return chip;
};

export const chipifyMergeTags = (root, getGroups) => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (
      node.nodeValue.includes("({") &&
      !node.parentElement?.closest(".cf-merge-chip, script, style, textarea")
    ) {
      nodes.push(node);
    }
  }

  if (nodes.length === 0) return;
  const groups = getGroups();
  nodes.forEach((node) => {
    const text = node.nodeValue;
    const fragment = doc.createDocumentFragment();
    let last = 0;
    for (const match of text.matchAll(TAG_PATTERN)) {
      fragment.append(
        text.slice(last, match.index),
        createMergeTagChip(doc, match[0], groups)
      );
      last = match.index + match[0].length;
    }
    if (last === 0) return;
    fragment.append(text.slice(last));
    node.replaceWith(fragment);
  });
};

// Attribute serialization escapes quotes; text serialization escapes < and >.
export const unchipifyMergeTags = (html) =>
  typeof html === "string" && html.includes("data-merge-tag")
    ? html.replace(CHIP_HTML, (chip, text) =>
        text
          .replace(/&quot;/g, '"')
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
      )
    : html;

export const removeChipFromHtml = (html, root, chip) => {
  const template = document.createElement("template");
  template.innerHTML = html;
  chipifyMergeTags(template.content, () => []);
  const rendered = [...root.querySelectorAll(".cf-merge-chip")];
  const saved = template.content.querySelectorAll(".cf-merge-chip");
  const match = saved[rendered.indexOf(chip)];
  if (
    saved.length !== rendered.length ||
    match?.dataset.mergeTag !== chip.dataset.mergeTag
  ) {
    return html;
  }
  match.remove();
  return unchipifyMergeTags(template.innerHTML);
};

// The canvas renders these areas via dangerouslySetInnerHTML, so React only
// rewrites them when the html changes, and that write is observed again.
export const observeMergeTagChips = (canvas, getGroups) => {
  const chipifyWithin = (node, groups) => {
    const element = node.nodeType === 1 ? node : node.parentElement;
    if (!element || element.closest(".fr-element")) return;
    const scopes = element.closest(".cf-merge-tag-allowed")
      ? [element]
      : element.querySelectorAll(".cf-merge-tag-allowed");
    scopes.forEach((scope) => {
      if (!scope.querySelector(".fr-element")) {
        chipifyMergeTags(scope, groups);
      }
    });
  };

  const batchGroups = () => {
    let groups;
    return () => (groups ??= getGroups());
  };

  chipifyWithin(canvas, batchGroups());
  const observer = new MutationObserver((mutations) => {
    const groups = batchGroups();
    mutations.forEach((mutation) =>
      mutation.addedNodes.forEach((node) => {
        if (node.isConnected && node.textContent.includes("({")) {
          chipifyWithin(node, groups);
        }
      })
    );
  });
  observer.observe(canvas, { childList: true, subtree: true });
  return () => observer.disconnect();
};
